import { startCase, uniqueId } from 'lodash'
import type {
  BasicFeeType,
  BookType,
  InvoiceDetail,
  InvoiceTransaction,
  ManualAccessorial,
  ThresholdFeeType,
  TransactionRow,
} from '@/api/useFreight/types'
import { sortByName } from '@/components/Reports/TankSupplyDefaults/helpers'
import type {
  AccessorialOptionType,
  FreightLineItemForm,
  FLIFormType,
  AccessorialFormType,
  BaseFreightFormType,
} from './types'
import type { TransactionDetail } from '../columnDefs'

export function constructRebillDetailPayload(
  type: FLIFormType,
  selectedLineItem: { label: string; feeType: string; requiresApproval: boolean },
  data: TransactionRow | InvoiceTransaction,
  values: FreightLineItemForm,
  productGroup: string | null,
  finalRate: number,
  finalAmount: number,
  finalTotal: number,
  bookType: string
) {
  return {
    id: uniqueId('dummy-'),
    type: startCase(type),
    subtype: values.base_freight_subtype || selectedLineItem.label,
    subtype_original: values.base_freight_subtype || selectedLineItem.label,
    subtype_override: null,
    po: null,
    counterparty: bookType === 'Revenue' ? data.customer_name || '' : data.carrier_name || '',
    counterparty_id: bookType === 'Revenue' ? data.customer_id || '' : data.carrier_id || '',
    product_id: values.product?.value,
    product_id_original: values.product?.value,
    product_id_override: null,
    product_name: values.product?.label,
    product_name_original: values.product?.label,
    product_name_override: null,
    product_group: productGroup,
    product_group_original: productGroup,
    product_group_override: null,
    origin_id: values.origin?.value,
    origin_id_original: values.origin?.value,
    origin_id_override: null,
    origin_name: values.origin?.label,
    origin_name_original: values.origin?.label,
    origin_name_override: null,
    destination_id: values.destination?.value,
    destination_id_original: values.destination?.value,
    destination_id_override: null,
    destination_name: values.destination?.label,
    destination_name_original: values.destination?.label,
    destination_name_override: null,
    bol_number: values.bol_number,
    bol_number_original: values.bol_number,
    bol_number_override: null,
    amount: finalAmount,
    amount_original: finalAmount,
    amount_override: null,
    rate: finalRate,
    rate_original: finalRate,
    rate_override: null,
    uom: values.uom || selectedLineItem.feeType || selectedLineItem.uom,
    uom_original: values.uom || selectedLineItem.feeType || selectedLineItem.uom,
    uom_override: null,
    total: finalTotal,
    total_original: finalTotal,
    total_override: null,
    book_type: bookType,
    override_by: null,
    override_date: null,
    date: new Date().toISOString(),
    status: selectedLineItem.requiresApproval ? 'needs_approval' : 'ready',
    legs: [],

    credit_rebill_metadata: {
      impact_type: 'added',
      delta_base_total: 0,
      delta_surcharge_total: 0,
      delta_accessorial_total: finalTotal,
      delta_total: finalTotal,
      delta_amount: finalAmount,
    },
    order_number: data.order_number,
    manual: true,
  } as const
}

export function constructManualAccessorialPayload(formValues: AccessorialFormType, bookType: BookType) {
  return {
    accessorial_id: formValues.accessorial_id,
    book_type: bookType,
    amount: formValues.amount,
    origin_id: formValues.origin?.value,
    destination_id: formValues.destination?.value,
    ...(formValues.reason_code && { reason_code: formValues.reason_code }),
  }
}

export function constructBaseFreightPayload(
  bookType: BookType,
  order: TransactionRow | InvoiceTransaction | undefined,
  formValues: BaseFreightFormType
) {
  const { uom, base_freight_subtype, origin, destination, product, ...values } = formValues
  return {
    order_id: order?.order_id || '',
    customer_id: order?.customer_id || '',
    book_type: bookType,
    uom: uom,
    subtype: base_freight_subtype,
    origin_id: origin?.value,
    destination_id: destination?.value,
    product_id: product?.value,
    ...values,
  }
}

export function constructOverridePayload(
  transactionId: string,
  customerId: string,
  bookType: BookType,
  values: FreightLineItemForm
) {
  return {
    transaction_id: transactionId,
    total_override: values.total,
    amount_override: values.amount,
    rate_override: values.rate,
    subtype_override: values.base_freight_subtype,
    uom_override: values.uom,
    product_id_override: values.product?.value,
    origin_id_override: values.origin?.value,
    destination_id_override: values.destination?.value,
    bol_number_override: values.bol_number,
    book_type: bookType,
    customer_id: customerId,
  }
}

export function createOptionsByIdAndName(list: Array<{ id: string; name: string }>) {
  return list.sort(sortByName).map((item) => ({
    label: item.name,
    value: item.id,
  }))
}

export function getAccessorialOptions(accessorials: ManualAccessorial[]): AccessorialOptionType[] {
  return accessorials
    ?.map((acc) => ({
      label: acc.name,
      value: acc.id || acc._id,
      description: acc.description || '',
      feeType: acc.fee_type?.type || '',
      rate: acc.rate || 0,
      requiresApproval: acc.requires_approval || false,
      disabled: acc.disabled,
      requiresReasonCode: acc.requires_reason_code || false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function getHelperText(overrideType: 'amount' | 'total', isPercentage: boolean) {
  if (overrideType === 'amount' && !isPercentage) {
    return 'Total will recalculate based on the new amount.'
  }
  if (overrideType === 'total') {
    return isPercentage
      ? 'Amount is calculated based on the other line items.'
      : 'Amount will reset to the original calculated value.'
  }
  return ''
}

export function findProductTypeById<T = string | number>(
  map: Record<string, Array<{ id: T; name?: string }>>,
  id: T
): string | null {
  for (const [type, items] of Object.entries(map)) {
    if (items.some((it) => it.id === id)) return type
  }
  return null
}

export const getPercentageMessage = (feeType: BasicFeeType | ThresholdFeeType | string): string => {
  switch (feeType) {
    case 'Base Freight %':
      return 'This percentage rate will be calculated after save & applied to all base freight rate line items.'
    case 'Base & Accessorial %':
      return 'This percentage rate will be calculated after save & applied to all freight rate and accessorial line items.'
    case 'Total Freight %':
      return 'This percentage rate will be calculated after save & applied to the total of all line items.'
    default:
      return 'This percentage rate will be automatically applied.'
  }
}

export const calculatePercentageTotal = (
  feeType: BasicFeeType | ThresholdFeeType,
  rate: number,
  details: InvoiceDetail[] | TransactionDetail[] | undefined
): number => {
  if (!details || details.length === 0) return 0

  let baseAmount = 0

  if (feeType === 'Base Freight %') {
    baseAmount = details.reduce((sum, detail) => {
      if (detail.type === 'Freight Rate' || detail.type === 'Point To Point') {
        return sum + (detail.total || 0)
      }
      return sum
    }, 0)
  } else if (feeType === 'Base & Accessorial %') {
    baseAmount = details.reduce((sum, detail) => {
      if (detail.type === 'Freight Rate' || detail.type === 'Point To Point' || detail.type === 'Accessorial') {
        return sum + (detail.total || 0)
      }
      return sum
    }, 0)
  } else if (feeType === 'Total Freight %') {
    baseAmount = details.reduce((sum, detail) => sum + (detail.total || 0), 0)
  }

  return baseAmount * (rate / 100)
}
